// Sprite anchoring — the pose-change mismatch fix (2026-07 metrics pass).
//
// Every roster/base PNG is alpha-trimmed to a full-height 640px canvas, but the
// TRIM-BOX WIDTH swings per pose, so the old "center the canvas on the fighter
// x" draw popped the character sideways on every pose swap (the mismatch is
// horizontal, not vertical). tools/sprite-metrics.mjs measured each sheet's
// true feet row (feetY), character height (charH) and stance center (centerX =
// lower-third center of mass); this module turns those numbers into a draw
// placement so that:
//   (a) the FEET always land on the fighter's floor line regardless of the
//       PNG's trim box,
//   (b) the on-screen character height is CONSTANT per class — the class
//       idle's normalized height; each pose scales by idleCharH / poseCharH,
//   (c) the stance center sits exactly on the fighter's x (no lateral pops),
//       which also makes the facing mirror flip around the STANCE center, not
//       the trim-box center.
// Pure math + JSON — no DOM — so unit tests run in node. Callers pass a
// reusable `out` object (the renderer keeps one module-level instance) so the
// per-frame hot path allocates nothing.

import metricsJson from "../assets/sprite-metrics.json";

export interface SpriteMetrics {
  /** PNG canvas size at metric time (guards stale metrics after a repaint). */
  w: number;
  h: number;
  /** Lowest row with >=3% opaque pixels in the central 60% band (floor contact). */
  feetY: number;
  /** feetY → head-top extent — the character's true height in source px. */
  charH: number;
  /** Center of mass of the lower third — the stance center in source px. */
  centerX: number;
}

/** Draw placement in fighter-local px (origin = fighter x on the floor line). */
export interface AnchorPlace {
  x: number;
  y: number;
  w: number;
  h: number;
}

// The JSON carries a "_generated" header string next to the metric entries.
const METRICS = metricsJson as Record<string, SpriteMetrics | string>;

/** Metrics for "<class>:<sheet>" / "base:<idle|rock|paper|scissors>", or null. */
export function metricsFor(key: string): SpriteMetrics | null {
  const m = METRICS[key];
  return m !== undefined && typeof m !== "string" ? m : null;
}

/**
 * Computes where to draw a sheet, writing into (and returning) `out`.
 *
 * `sheetKey`/`idleKey` are metric keys; `imgW`/`imgH` are the ACTUAL image
 * dimensions (a mismatch with the recorded metrics means the art was
 * regenerated without re-running tools/sprite-metrics.mjs — the placement
 * falls back to the legacy centered fit rather than anchoring on stale rows).
 * `targetH` is the on-screen height budget ((EXT_T+EXT_B)*scale) and `footY`
 * the local y of the floor contact (EXT_B*scale) — both straight from the old
 * draw math so the idle pose renders at its established size.
 */
export function anchorPlacement(
  sheetKey: string,
  idleKey: string,
  imgW: number,
  imgH: number,
  targetH: number,
  footY: number,
  out: AnchorPlace,
): AnchorPlace {
  const m = metricsFor(sheetKey);
  if (!m || m.charH <= 0 || m.h <= 0 || imgW !== m.w || imgH !== m.h) {
    // Legacy centered fit (no/stale metrics): full canvas into the height box.
    out.h = targetH;
    out.w = imgH > 0 ? (imgW / imgH) * targetH : targetH;
    out.x = -out.w / 2;
    out.y = footY - targetH;
    return out;
  }
  const mi = metricsFor(idleKey) ?? m;
  // Class-constant on-screen character height: the idle sheet's character as
  // drawn into the legacy targetH box (keeps established idle sizing), then
  // every pose scales by idleCharH/poseCharH to match it.
  const charHpx = mi.h > 0 && mi.charH > 0 ? targetH * (mi.charH / mi.h) : targetH;
  const s = charHpx / m.charH;
  out.w = m.w * s;
  out.h = m.h * s;
  out.x = -m.centerX * s; // stance center → local x 0 (mirror-safe)
  out.y = footY - (m.feetY + 1) * s; // feet row bottom → the floor line
  return out;
}
